"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { IconChevronDown, IconSend } from "@tabler/icons-react";
import {
  budgetLinePrice,
  capitalize,
  getCreatorSmsPhone,
  resolveSmsTemplatePlaceholders,
} from "@/lib/util";
import {
  sendEngagementMessage,
  updateEngagementChannel,
  useEngagementMessages,
  useEngagements,
  useInstagramRecipients,
  useSmsTemplates,
  type EngagementMessage,
  type OutreachChannel,
} from "@/services/engagement";
import { toast } from "sonner";

type EngagementLike = {
  id: string;
  offerToken?: string;
  creatorPriceId: number;
  customPrice?: number | null;
  outreachChannel?: OutreachChannel;
  recipientIgScopedId?: string | null;
  instagramAccountId?: string | null;
  creatorPrice: {
    username?: string;
    platform?: string;
    creator: {
      id: number;
      name?: string;
      smsOptIn?: boolean;
      avatar?: string;
      imessageCapable?: boolean | null;
    };
  };
};

export type EngagementChatDialogProps = {
  open: boolean;
  engagementId: string | null;
  campaign: Campaign;
  onOpenChange: (open: boolean) => void;
  onSent?: () => void;
};

function providerLabel(provider: string | null | undefined): string {
  if (provider === "blooio") return "iMessage";
  if (provider === "sendblue") return "iMessage";
  if (provider === "twilio") return "SMS";
  return provider || "—";
}

/** Pill chip for status/provider on a message bubble — inherits the bubble's text colour so it stays readable on any background. */
function MetaChip({ children }: { children: React.ReactNode }) {
  return (
    <Box
      as="span"
      style={{
        display: "inline-block",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: "16px",
        padding: "2px 8px",
        borderRadius: 9999,
        background: "rgba(0, 0, 0, 0.12)",
        border: "1px solid rgba(0, 0, 0, 0.18)",
        color: "inherit",
      }}
    >
      {children}
    </Box>
  );
}

export default function EngagementChatDialog({
  open,
  engagementId,
  campaign,
  onOpenChange,
  onSent,
}: EngagementChatDialogProps) {
  const { data: engagements } = useEngagements(open ? campaign.id : undefined);
  const eng = useMemo(() => {
    if (!engagementId || !Array.isArray(engagements)) return null;
    return (
      (engagements as EngagementLike[]).find((e) => e.id === engagementId) ??
      null
    );
  }, [engagements, engagementId]);

  const { data: messages, mutate: mutateMessages } = useEngagementMessages(
    open ? (engagementId ?? undefined) : undefined,
    { refreshInterval: open && engagementId ? 5000 : 0 },
  );

  const { data: templates } = useSmsTemplates(open);

  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [switchingChannel, setSwitchingChannel] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  const isInstagram = eng?.outreachChannel === "Instagram";
  const { data: igRecipients } = useInstagramRecipients(
    eng?.creatorPriceId,
    open && !!eng && !isInstagram,
  );

  const baseUrl = (process.env.NEXT_PUBLIC_DOMAIN || "").replace(/\/$/, "");

  const templateVars = useMemo(() => {
    if (!eng) return null;
    const offerToken = eng.offerToken ?? "";
    const offerLink = offerToken
      ? `${baseUrl}/offer/${offerToken}`
      : `${baseUrl}/offer/`;
    return {
      creatorName: eng.creatorPrice.creator?.name ?? "",
      campaignName: campaign.name ?? "",
      offerLink,
      platform: eng.creatorPrice.platform ?? "",
      price: String(
        budgetLinePrice(campaign, eng.creatorPriceId, eng.customPrice),
      ),
    };
  }, [eng, campaign, baseUrl]);

  useEffect(() => {
    if (open && engagementId) {
      setBody("");
    }
  }, [open, engagementId]);

  const scrollToBottom = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!open) return;
    scrollToBottom();
  }, [open, messages, scrollToBottom]);

  const sortedMessages = useMemo(() => {
    if (!messages?.length) return [];
    return [...messages].sort(
      (a: EngagementMessage, b: EngagementMessage) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [messages]);

  const handleSwitchToInstagram = useCallback(async () => {
    if (!engagementId || !igRecipients?.matches?.length || !igRecipients.accounts?.length) {
      toast.error("No Instagram conversation found for this creator");
      return;
    }
    const match = igRecipients.matches[0];
    const account =
      igRecipients.accounts.find((a) => a.id === match.igAccountId) ??
      igRecipients.accounts[0];
    setSwitchingChannel(true);
    try {
      await updateEngagementChannel(engagementId, {
        outreachChannel: "Instagram",
        instagramAccountId: account.id,
        recipientIgScopedId: match.participantIgScopedId,
        instagramConversationId: match.id,
      });
      toast.success("Switched to Instagram DM");
      onSent?.();
    } catch (e) {
      console.error(e);
      toast.error("Could not switch channel");
    } finally {
      setSwitchingChannel(false);
    }
  }, [engagementId, igRecipients, onSent]);

  const handleSwitchToSms = useCallback(async () => {
    if (!engagementId) return;
    setSwitchingChannel(true);
    try {
      await updateEngagementChannel(engagementId, { outreachChannel: "Sms" });
      toast.success("Switched to SMS");
      onSent?.();
    } catch (e) {
      console.error(e);
      toast.error("Could not switch channel");
    } finally {
      setSwitchingChannel(false);
    }
  }, [engagementId, onSent]);

  const handleSend = useCallback(async () => {
    if (!engagementId || !body.trim()) return;
    const text = body.trim();
    const now = new Date().toISOString();
    const optimisticId = `optimistic-${Date.now()}`;
    const optimistic: EngagementMessage = isInstagram
      ? {
          kind: "instagram",
          id: optimisticId,
          createdAt: now,
          direction: "outbound",
          body: text,
          status: "sending",
          sentBy: null,
          errorMessage: null,
          isHumanAgentReply: false,
          mid: optimisticId,
        }
      : {
          kind: "sms",
          id: optimisticId,
          createdAt: now,
          direction: "outbound",
          body: text,
          status: "sending",
          provider: null,
          sentBy: null,
          errorMessage: null,
          templateId: null,
        };

    // Optimistically render the message immediately, then reconcile with the
    // server once the (potentially slow) upstream send round-trip completes.
    setBody("");
    setSending(true);
    try {
      await mutateMessages(
        (async () => {
          await sendEngagementMessage(engagementId, { body: text });
          return undefined;
        })(),
        {
          optimisticData: (current?: EngagementMessage[]) => [
            ...(current ?? []),
            optimistic,
          ],
          populateCache: false,
          revalidate: true,
          rollbackOnError: true,
        },
      );
      onSent?.();
    } catch (e) {
      console.error(e);
      const msg =
        e && typeof e === "object" && "message" in e && typeof (e as { message: unknown }).message === "string"
          ? (e as { message: string }).message
          : "Failed to send message";
      toast.error(msg);
      setBody(text);
    } finally {
      setSending(false);
    }
  }, [engagementId, body, isInstagram, mutateMessages, onSent]);

  const applyTemplate = (templateBody: string) => {
    if (!templateVars) return;
    setBody(resolveSmsTemplatePlaceholders(templateBody, templateVars));
  };

  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!sending && body.trim()) {
        void handleSend();
      }
    }
  };

  const creator = eng?.creatorPrice.creator;
  const phone = creator ? getCreatorSmsPhone(creator as any) : null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={{
          maxWidth: 520,
          width: "min(100vw - 24px, 520px)",
          height: "min(80vh, 720px)",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          gap: 0,
        }}
      >
        <Box p="4" style={{ borderBottom: "1px solid var(--gray-a6)" }}>
          <Flex align="start" gap="3">
            <Avatar
              size="3"
              src={
                creator?.avatar ? `/image-upload/${creator.avatar}` : undefined
              }
              fallback={(creator?.name || "?")[0]}
              radius="full"
            />
            <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
              <Dialog.Title style={{ margin: 0 }}>
                {eng?.creatorPrice.creator?.name ?? "Chat"}
              </Dialog.Title>
              <Text size="2" color="gray" style={{ wordBreak: "break-word" }}>
                @{eng?.creatorPrice.username} ·{" "}
                {capitalize(eng?.creatorPrice.platform || "")}
              </Text>
              {phone && (
                <Text size="1" color="gray">
                  {phone}
                </Text>
              )}
              <Flex gap="2" wrap="wrap" mt="1">
                <Badge color={isInstagram ? "purple" : "blue"} size="1" variant="soft">
                  {isInstagram ? "Instagram DM" : "SMS"}
                </Badge>
                {!isInstagram && (
                  <>
                    {creator?.smsOptIn ? (
                      <Badge color="green" size="1">
                        SMS opted in
                      </Badge>
                    ) : (
                      <Badge color="gray" size="1">
                        Not opted in
                      </Badge>
                    )}
                    {creator?.imessageCapable === true && (
                      <Badge color="blue" size="1" variant="soft">
                        iMessage
                      </Badge>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Box>

        <Box
          ref={threadRef}
          p="3"
          style={{
            flex: 1,
            overflowY: "auto",
            background: "var(--gray-a2)",
          }}
        >
          {!sortedMessages.length ? (
            <Text
              size="2"
              color="gray"
              align="center"
              as="p"
              style={{ padding: "var(--space-6)" }}
            >
              No messages yet. Send the first one below.
            </Text>
          ) : (
            <Flex direction="column" gap="2">
              {sortedMessages.map((msg) => {
                const outbound = msg.direction === "outbound";
                const channelLabel = msg.kind === "instagram" ? "IG" : providerLabel(msg.kind === "sms" ? msg.provider : null);
                return (
                  <Flex key={`${msg.kind}-${msg.id}`} justify={outbound ? "end" : "start"}>
                    <Box
                      style={{
                        maxWidth: "85%",
                        padding: "10px 12px",
                        borderRadius: outbound
                          ? "16px 16px 4px 16px"
                          : "16px 16px 16px 4px",
                        background: outbound
                          ? "var(--blue-9)"
                          : "var(--gray-2)",
                        color: outbound ? "var(--gray-1)" : "var(--gray-12)",
                        border: outbound ? "none" : "1px solid var(--gray-a6)",
                      }}
                    >
                      <Text
                        size="2"
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.45,
                          ...(outbound
                            ? { color: "var(--gray-1)" }
                            : {}),
                        }}
                      >
                        {msg.body || "(empty)"}
                      </Text>
                      <Flex gap="2" align="center" wrap="wrap" mt="2">
                        <Text
                          size="1"
                          style={{ color: "inherit", opacity: 0.7 }}
                        >
                          {new Date(msg.createdAt).toLocaleString()}
                        </Text>
                        <MetaChip>{channelLabel}</MetaChip>
                        {outbound && <MetaChip>{msg.status}</MetaChip>}
                        {msg.kind === "instagram" && msg.isHumanAgentReply && (
                          <MetaChip>HUMAN_AGENT</MetaChip>
                        )}
                      </Flex>
                      {msg.errorMessage && (
                        <Text size="1" color="red" mt="1" highContrast>
                          {msg.errorMessage}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                );
              })}
            </Flex>
          )}
        </Box>

        <Box p="3" style={{ borderTop: "1px solid var(--gray-a6)" }}>
          <Flex direction="column" gap="2">
            <Flex gap="2" wrap="wrap" align="center" justify="between">
              <Flex gap="2" wrap="wrap" align="center">
              {isInstagram ? (
                <Button
                  size="1"
                  variant="soft"
                  color="gray"
                  type="button"
                  disabled={switchingChannel}
                  onClick={() => void handleSwitchToSms()}
                >
                  Switch to SMS
                </Button>
              ) : (
                <Button
                  size="1"
                  variant="soft"
                  color="purple"
                  type="button"
                  disabled={
                    switchingChannel ||
                    !igRecipients?.matches?.length ||
                    !igRecipients?.accounts?.length
                  }
                  title={
                    !igRecipients?.matches?.length
                      ? "Creator must DM your IG account first"
                      : undefined
                  }
                  onClick={() => void handleSwitchToInstagram()}
                >
                  Switch to Instagram
                </Button>
              )}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <Flex
                    align="center"
                    gap="2"
                    py="2"
                    px="3"
                    style={{
                      cursor: "pointer",
                      borderRadius: "var(--radius-2)",
                      background: "var(--gray-a3)",
                      border: "1px solid var(--gray-a6)",
                    }}
                  >
                    <Text size="2">Insert template</Text>
                    <IconChevronDown size={16} />
                  </Flex>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  size="2"
                  style={{ maxHeight: 280, overflowY: "auto" }}
                >
                  {(templates || []).map(
                    (t: { id: string; name: string; body: string }) => (
                      <DropdownMenu.Item
                        key={t.id}
                        onSelect={() => applyTemplate(t.body)}
                      >
                        {t.name}
                      </DropdownMenu.Item>
                    ),
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
              </Flex>
            </Flex>
            {isInstagram && (
              <Text size="1" color="gray">
                Meta limits cold outreach to 1 tagged message per creator per 7 days. Replies
                open the standard 24h window.
              </Text>
            )}
            <TextArea
              rows={3}
              placeholder={
                isInstagram
                  ? "Instagram DM… (max 1000 characters)"
                  : "Type a message… Enter to send, Shift+Enter for newline"
              }
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={onComposerKeyDown}
            />
            <Flex justify="end" gap="2">
              <Dialog.Close>
                <Button variant="soft" color="gray" type="button">
                  Close
                </Button>
              </Dialog.Close>
              <Button
                type="button"
                onClick={() => void handleSend()}
                disabled={sending || !body.trim() || !engagementId}
              >
                <IconSend size={16} style={{ marginRight: 6 }} />
                {sending ? "Sending…" : "Send"}
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
}
